import React, { useState } from 'react';
import { Upload, ImageIcon, IndianRupee, FileText, Type, X, Save, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API || 'http://127.0.0.1:8000';

const AddCombo = ({ onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    image1: null,
    image2: null
  });
  const [previews, setPreviews] = useState({
    image1: null,
    image2: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleImageChange = (e, imageField) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      setFormData(prev => ({
        ...prev,
        [imageField]: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => ({
          ...prev,
          [imageField]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate form data
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Price must be a positive number');
      }
      if (!formData.image1) {
        throw new Error('At least one image is required');
      }

      // Prepare form data for API
      const apiFormData = new FormData();
      
      // Add combo data as JSON string
      const comboData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price)
      };
      apiFormData.append('combo', JSON.stringify(comboData));

      // Add images
      if (formData.image1) {
        apiFormData.append('image1', formData.image1);
      }
      if (formData.image2) {
        apiFormData.append('image2', formData.image2);
      }

      // Make API call
      const response = await fetch(`${API_BASE_URL}/combo/add/`, {
        method: 'POST',
        body: apiFormData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to add combo');
      }

      // Success
      setSuccess(`Combo "${result.title}" added successfully with ID: ${result.id}`);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        price: '',
        image1: null,
        image2: null
      });
      setPreviews({
        image1: null,
        image2: null
      });

      // Call onSave callback if provided
      if (onSave) {
        onSave(result);
      }

    } catch (error) {
      console.error('Error adding combo:', error);
      setError(error.message || 'Failed to add combo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (imageField) => {
    setFormData(prev => ({
      ...prev,
      [imageField]: null
    }));
    setPreviews(prev => ({
      ...prev,
      [imageField]: null
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      image1: null,
      image2: null
    });
    setPreviews({
      image1: null,
      image2: null
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <FileText className="h-6 w-6 text-indigo-600 mr-2" />
            Add New Combo
          </h2>
          <p className="text-sm text-gray-600 mt-1">Create a new combo package with images and details</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mx-6 mt-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{success}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              {/* Title Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Type className="h-4 w-4 inline mr-1" />
                  Combo Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter combo title..."
                  required
                />
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter combo description..."
                  required
                />
              </div>

              {/* Price Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <IndianRupee className="h-4 w-4 inline mr-1" />
                  Price *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  <ImageIcon className="h-4 w-4 inline mr-1" />
                  Combo Images *
                </label>
                
                <div className="space-y-4">
                  {/* Image 1 */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="text-center">
                      {previews.image1 ? (
                        <div className="relative">
                          <img
                            src={previews.image1}
                            alt="Preview 1"
                            className="w-full h-40 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage('image1')}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="mx-auto h-10 w-10 text-gray-400" />
                          <div className="mt-2">
                            <label className="cursor-pointer">
                              <span className="text-sm text-indigo-600 hover:text-indigo-500 font-medium">
                                Upload Primary Image *
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(e, 'image1')}
                                className="hidden"
                              />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image 2 */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="text-center">
                      {previews.image2 ? (
                        <div className="relative">
                          <img
                            src={previews.image2}
                            alt="Preview 2"
                            className="w-full h-40 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage('image2')}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="mx-auto h-10 w-10 text-gray-400" />
                          <div className="mt-2">
                            <label className="cursor-pointer">
                              <span className="text-sm text-indigo-600 hover:text-indigo-500 font-medium">
                                Upload Secondary Image
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(e, 'image2')}
                                className="hidden"
                              />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB (Optional)</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={resetForm}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Adding Combo...
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  Add Combo
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCombo;